'use client';

import { useEffect } from 'react';
import { EntityContextType, useAuthStore, Operation } from '@/stores/authStore';
import { PrivilegeEnum } from '@/api/ApiClient';

import { log } from '@/lib/utils/logger';

/**
 * Custom hook for unified privilege checking.
 * Combines static privileges from JWT with dynamic entity-specific privileges.
 *
 * Priority order:
 * 1. Static privileges from JWT (unconditional)
 * 2. Entity-specific privileges from getAccessRights (add only)
 */
export const usePrivileges = (entityType?: EntityContextType, entityId?: string) => {
  const {
    isAuthorized: isAuthorizedStatic,
    hasAnyPrivilege: hasAnyPrivilegeStatic,
    isAuthorizedInEntity: isAuthorizedInEntityFromStore,
    loadEntityPrivileges,
  } = useAuthStore();

  // Load entity-specific privileges if entityType and entityId are provided
  useEffect(() => {
    if (entityType && entityId) {
      log.debug(`[usePrivileges] Loading entity privileges for ${entityType} with ID ${entityId}`);
      loadEntityPrivileges(entityId, entityType);
    }
  }, [loadEntityPrivileges, entityType, entityId]);
  /**
   * Check if user is authorized for a specific privilege and operation.
   * Global JWT is checked first; entity-specific privileges from getAccessRights only add.
   */
  const isAuthorized = (privilege: PrivilegeEnum | null | undefined, op: Operation): boolean => {
    // Use static authorization first (store denies null/undefined)
    // Global JWT wins. Entity-specific privileges only apply when JWT does not grant the op.
    if (isAuthorizedStatic(privilege, op)) {
      return true;
    } else {
      return isAuthorizedInEntityFromStore(privilege, op);
    }
  };

  /**
   * Check if user has any of the specified privileges.
   * Global JWT is checked first; entity-specific privileges from getAccessRights only add.
   */
  const hasAnyPrivilege = (privileges: { p: PrivilegeEnum; op?: Operation }[]): boolean => {
    if (hasAnyPrivilegeStatic(privileges)) {
      return true;
    }
    if (entityType && entityId) {
      return privileges.some((privilege) => isAuthorizedInEntityFromStore(privilege.p, privilege.op ?? 'read'));
    }
    return false;
  };

  return {
    isAuthorized,
    hasAnyPrivilege,
  };
};
